import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Database, FileDown, Upload, Trash2, Check, X, Edit2
} from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const categoryOptions = [
  { value: 'bom', label: 'Materials (BOM)', color: 'bg-blue-500/20 text-blue-400' },
  { value: 'energy', label: 'Energy', color: 'bg-yellow-500/20 text-yellow-400' },
  { value: 'transport', label: 'Transport', color: 'bg-green-500/20 text-green-400' },
  { value: 'process', label: 'Process', color: 'bg-purple-500/20 text-purple-400' },
  // Gate-to-Grave categories
  { value: 'use_phase', label: 'Use Phase', color: 'bg-cyan-500/20 text-cyan-400' },
  { value: 'maintenance', label: 'Maintenance', color: 'bg-orange-500/20 text-orange-400' },
  { value: 'eol', label: 'End-of-Life', color: 'bg-rose-500/20 text-rose-400' }
];

export default function DatasetLibrary() {
  const navigate = useNavigate();
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [newRow, setNewRow] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDatasets();
  }, []);

  const fetchDatasets = async () => {
    try {
      const response = await api.get('/datasets');
      setDatasets(response.data);
    } catch (error) {
      toast.error('Failed to load datasets');
    } finally {
      setLoading(false);
    }
  };

  const seedDatasets = async () => {
    try {
      await api.post('/seed-datasets');
      toast.success('Sample datasets loaded');
      fetchDatasets();
    } catch (error) {
      toast.error('Failed to seed datasets');
    }
  };

  const getEmptyRow = () => ({
    name: '',
    category: 'bom',
    unit: '',
    emission_factor: '',
    geography: 'Global',
    source: ''
  });

  const addNewRow = () => {
    setNewRow(getEmptyRow());
  };

  const cancelNewRow = () => {
    setNewRow(null);
  };

  const saveNewRow = async () => {
    if (!newRow.name || !newRow.unit || !newRow.emission_factor) {
      toast.error('Name, Unit, and Emission Factor are required');
      return;
    }

    try {
      const response = await api.post('/datasets/bulk', {
        name: newRow.name,
        category: newRow.category,
        unit: newRow.unit,
        emission_factor: parseFloat(newRow.emission_factor),
        geography: newRow.geography || 'Global',
        source: newRow.source || 'User uploaded'
      });
      setDatasets([...datasets, response.data]);
      setNewRow(null);
      toast.success('Dataset added');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dataset');
    }
  };

  const startEditing = (item) => {
    setEditingRowId(item.id);
    setEditingData({
      name: item.name,
      category: item.category,
      unit: item.unit,
      emission_factor: item.emission_factor,
      geography: item.geography,
      source: item.source
    });
  };

  const cancelEditing = () => {
    setEditingRowId(null);
    setEditingData({});
  };

  const saveEditing = async (itemId) => {
    if (!editingData.name || !editingData.unit || !editingData.emission_factor) {
      toast.error('Name, Unit, and Emission Factor are required');
      return;
    }

    try {
      const response = await api.put(`/datasets/${itemId}`, {
        name: editingData.name,
        category: editingData.category,
        unit: editingData.unit,
        emission_factor: parseFloat(editingData.emission_factor),
        geography: editingData.geography || 'Global',
        source: editingData.source || 'User uploaded'
      });
      setDatasets(datasets.map(d => d.id === itemId ? response.data : d));
      setEditingRowId(null);
      setEditingData({});
      toast.success('Dataset updated');
    } catch (error) {
      toast.error('Failed to update dataset');
    }
  };

  const deleteDataset = async (itemId) => {
    try {
      await api.delete(`/datasets/${itemId}`);
      setDatasets(datasets.filter(d => d.id !== itemId));
      toast.success('Dataset deleted');
    } catch (error) {
      toast.error('Failed to delete dataset');
    }
  };

  const downloadTemplate = () => {
    const token = localStorage.getItem('token');
    fetch(`${BACKEND_URL}/api/datasets/template`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
      .then(response => response.blob())
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'dataset_template.csv';
        a.click();
        URL.revokeObjectURL(url);
        toast.success('Template downloaded');
      })
      .catch(() => toast.error('Failed to download template'));
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/datasets/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.items_created > 0) {
        toast.success(`${response.data.items_created} datasets imported`);
        fetchDatasets();
      }
      if (response.data.errors?.length > 0) {
        toast.error(`${response.data.errors.length} errors during import`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload CSV');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const filteredDatasets = datasets.filter(ds => {
    const matchesSearch = ds.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         ds.source.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || ds.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const getCategoryBadge = (category) => {
    const cat = categoryOptions.find(c => c.value === category);
    return cat?.color || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="datasets-page">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/')} data-testid="back-btn" className="text-slate-500 hover:text-sky-700 hover:bg-sky-50">
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-3">
              <img src="/saarv-logo.svg" alt="Saarv" className="h-12 w-auto" />
              <div>
                <h1 className="text-lg font-bold text-slate-900">Dataset Library</h1>
                <p className="text-xs text-slate-500">LCI Emission Factors</p>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {datasets.length === 0 && (
              <Button variant="outline" onClick={seedDatasets} data-testid="seed-datasets-btn" className="border-slate-300 text-slate-700 hover:bg-slate-100">
                <Upload className="h-4 w-4 mr-2" />
                Load Sample Data
              </Button>
            )}
            <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn" className="border-slate-300 text-slate-700 hover:bg-slate-100">
              <FileDown className="h-4 w-4 mr-2" />
              Template
            </Button>
            <input type="file" ref={fileInputRef} accept=".csv" onChange={handleFileUpload} className="hidden" />
            <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-csv-btn" className="bg-sky-600 hover:bg-sky-700 text-white">
              {uploading ? <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Uploading...' : 'Upload CSV'}
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Input
              placeholder="Search datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-4 border-slate-300 bg-white"
              data-testid="dataset-search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px] border-slate-300 bg-white" data-testid="category-filter-select">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All Categories</SelectItem>
              {categoryOptions.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Dataset Table */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary mx-auto"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 border-b border-border">
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Name *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-32">Category *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-20">Unit *</th>
                      <th className="px-3 py-3 text-right font-semibold text-muted-foreground w-32">EF (kg CO₂e) *</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground w-24">Geography</th>
                      <th className="px-3 py-3 text-left font-semibold text-muted-foreground">Source</th>
                      <th className="px-3 py-3 text-center font-semibold text-muted-foreground w-24">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDatasets.map((ds, idx) => (
                      <tr key={ds.id} className="border-b border-border/50 hover:bg-muted/30" data-testid={`dataset-row-${ds.id}`}>
                        {editingRowId === ds.id ? (
                          // Editing mode
                          <>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.name}
                                onChange={(e) => setEditingData({...editingData, name: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Select value={editingData.category} onValueChange={(v) => setEditingData({...editingData, category: v})}>
                                <SelectTrigger className="h-8 text-sm">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {categoryOptions.map(cat => (
                                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.unit}
                                onChange={(e) => setEditingData({...editingData, unit: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                type="number"
                                step="0.0001"
                                value={editingData.emission_factor}
                                onChange={(e) => setEditingData({...editingData, emission_factor: e.target.value})}
                                className="h-8 text-sm text-right"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.geography}
                                onChange={(e) => setEditingData({...editingData, geography: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <Input
                                value={editingData.source}
                                onChange={(e) => setEditingData({...editingData, source: e.target.value})}
                                className="h-8 text-sm"
                              />
                            </td>
                            <td className="px-2 py-2">
                              <div className="flex items-center justify-center gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" onClick={() => saveEditing(ds.id)}>
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelEditing}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </>
                        ) : (
                          // Display mode
                          <>
                            <td className="px-3 py-3 font-medium">{ds.name}</td>
                            <td className="px-3 py-3">
                              <Badge className={getCategoryBadge(ds.category)} variant="secondary">
                                {ds.category.toUpperCase()}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 font-mono">{ds.unit}</td>
                            <td className="px-3 py-3 text-right font-mono">{ds.emission_factor}</td>
                            <td className="px-3 py-3">{ds.geography}</td>
                            <td className="px-3 py-3 text-muted-foreground">{ds.source}</td>
                            <td className="px-3 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => startEditing(ds)} data-testid={`edit-${ds.id}`}>
                                  <Edit2 className="h-3.5 w-3.5" />
                                </Button>
                                <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => deleteDataset(ds.id)} data-testid={`delete-${ds.id}`}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}

                    {/* New Row Input */}
                    {newRow && (
                      <tr className="bg-primary/5 border-b border-primary/20">
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.name}
                            onChange={(e) => setNewRow({...newRow, name: e.target.value})}
                            placeholder="Dataset name"
                            className="h-8 text-sm"
                            autoFocus
                            data-testid="new-row-name"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Select value={newRow.category} onValueChange={(v) => setNewRow({...newRow, category: v})}>
                            <SelectTrigger className="h-8 text-sm" data-testid="new-row-category">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {categoryOptions.map(cat => (
                                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.unit}
                            onChange={(e) => setNewRow({...newRow, unit: e.target.value})}
                            placeholder="kg"
                            className="h-8 text-sm"
                            data-testid="new-row-unit"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            type="number"
                            step="0.0001"
                            value={newRow.emission_factor}
                            onChange={(e) => setNewRow({...newRow, emission_factor: e.target.value})}
                            placeholder="0.0"
                            className="h-8 text-sm text-right"
                            data-testid="new-row-ef"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.geography}
                            onChange={(e) => setNewRow({...newRow, geography: e.target.value})}
                            placeholder="Global"
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <Input
                            value={newRow.source}
                            onChange={(e) => setNewRow({...newRow, source: e.target.value})}
                            placeholder="Source"
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="px-2 py-2">
                          <div className="flex items-center justify-center gap-1">
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" onClick={saveNewRow} data-testid="save-new-row">
                              <Check className="h-4 w-4" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground" onClick={cancelNewRow}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {/* Empty State */}
                    {filteredDatasets.length === 0 && !newRow && (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">
                          <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p>{datasets.length === 0 ? 'No datasets yet. Load sample data or add custom datasets.' : 'No datasets match your search.'}</p>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Row Button */}
        {!newRow && (
          <Button variant="outline" className="w-full mt-3 border-dashed" onClick={addNewRow} data-testid="add-row-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Row
          </Button>
        )}

        <p className="text-xs text-muted-foreground mt-4 text-center">
          {filteredDatasets.length} of {datasets.length} datasets shown
        </p>
      </main>
    </div>
  );
}
